import { useState } from 'react';
import { Link } from 'react-router-dom';
import FadeInSection from '@/components/base/FadeInSection';

const galleryImages = [
  {
    src: "/images/home-gallery-preview-1.jpg",
    alt: "Fresh vegetables at Bassi Enterprises",
    title: "Fresh Vegetables",
  },
  {
    src: "/images/home-gallery-preview-2.jpg",
    alt: "Fresh fruits at Bassi Enterprises",
    title: "Fresh Fruits",
  },
  {
    src: "/images/home-gallery-preview-3.jpg",
    alt: "Packing process at Bassi Enterprises",
    title: "Packing Process",
  },
  {
    src: "/images/home-gallery-preview-4.jpg",
    alt: "Supply crates at Bassi Enterprises",
    title: "Supply Crates",
  },
  {
    src: "/images/home-gallery-preview-5.jpg",
    alt: "Delivery preparation at Bassi Enterprises",
    title: "Delivery Preparation",
  },
  {
    src: "/images/home-gallery-preview-6.jpg",
    alt: "Warehouse at Bassi Enterprises",
    title: "Our Warehouse",
  },
];

export default function GalleryPreview() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-100">
      <FadeInSection>
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 bg-secondary-100 text-secondary-800 text-xs font-semibold rounded-full mb-4">
              Our Work
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Photo <strong className="text-primary-600">Gallery</strong>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              A glimpse into our operations — from sourcing fresh produce to delivering quality to your business.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={image.src}
                className="group relative rounded-xl overflow-hidden cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    title={image.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-end p-4">
                  <span className="text-background-50 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                    {image.title}
                  </span>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <i className="ri-zoom-in-line text-foreground-700 text-sm"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-semibold transition-colors duration-200"
            >
              View Full Gallery
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </FadeInSection>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-background-50/10 flex items-center justify-center hover:bg-background-50/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-close-line text-background-50 text-xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-3 md:left-6 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 flex items-center justify-center hover:bg-background-50/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-background-50 text-xl md:text-2xl"></i>
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-background-50/70 text-sm mt-3">
              {galleryImages[lightboxIndex].title} — {lightboxIndex + 1} / {galleryImages.length}
            </p>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-3 md:right-6 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 flex items-center justify-center hover:bg-background-50/20 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-background-50 text-xl md:text-2xl"></i>
          </button>
        </div>
      )}
    </section>
  );
}
